import {
  AttachmentReference,
  AttachmentReferenceTypeEnum,
  FilerAttachmentReference,
  Maybe,
  UriAttachmentReference,
} from '@vendorflow/types'
import { getAttachmentHrefFromAttachmentReference } from 'pages/WorkspacePage/ChannelView/MessageRenderers/MessageRenderers.services'
import { QueryCacheEnum } from 'constants/ReactQuery.constants'
import { useQueries, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useMyIdentityId } from 'queries/Auth.queries'
import { useFirebaseContext } from 'features/firebase/FirebaseProvider'
import { handleAttachmentRefFormat } from 'services/Utils.service'
import { getAttachmentDownloadedPath } from 'features/firebase/Firebase.services'
import { useEffect, useState } from 'react'
import { LONG_CACHE_TIME, LONG_STALE_TIME } from 'pages/WorkspacePage/Workspace.constants'
import ms from 'ms'

export function getAttachmentRefId(ref: AttachmentReference) {
  const attachmentType = ref['@type']
  return attachmentType === AttachmentReferenceTypeEnum.FILER
    ? (ref as FilerAttachmentReference).id
    : (ref as UriAttachmentReference).href
}

export function getUseAttachmentHrefQueryKey(ref: Maybe<AttachmentReference>) {
  const attachmentType = ref ? ref['@type'] : null
  const identifier = ref ? getAttachmentRefId(ref) : null
  return [QueryCacheEnum.ATTACHMENTS, attachmentType, identifier]
}

export function getUseDownloadedAttachmentHrefQueryKey(ref: Maybe<AttachmentReference>) {
  const identifier = ref ? getAttachmentRefId(ref) : null
  return [QueryCacheEnum.DOWNLOADED_ATTACHMENTS, identifier]
}

export function useAttachmentHref(ref: Maybe<AttachmentReference>) {
  return useQuery(
    getUseAttachmentHrefQueryKey(ref),
    () => (ref ? getAttachmentHrefFromAttachmentReference(ref) : null),
    { cacheTime: ms('1d'), staleTime: ms('1d') }
  )
}

export function useFrdDownloadedAttachments(refs: AttachmentReference[], options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true
  const bobId = useMyIdentityId()
  const { getFirebaseData } = useFirebaseContext()
  const [dataUpdatedAt, setDataUpdatedAt] = useState<number>(0)

  const result = useQueries({
    queries: refs.map((ref) => ({
      queryKey: getUseDownloadedAttachmentHrefQueryKey(ref),
      queryFn: async ({ queryKey }: { queryKey: (string | null)[] }) => {
        const formattedRefId = handleAttachmentRefFormat(queryKey[1]!)
        const downloadedRef = await getFirebaseData<number | null>(getAttachmentDownloadedPath(formattedRefId, bobId))
        return { id: formattedRefId, downloaded: Boolean(downloadedRef) }
      },
      cacheTime: LONG_CACHE_TIME,
      staleTime: LONG_STALE_TIME,
      enabled: Boolean(bobId && enabled),
    })),
  })
  const data = result.reduce<{ [key: string]: boolean }>((acc, item) => {
    if (item.data) {
      acc[item.data.id] = item.data.downloaded
    }
    return acc
  }, {})

  const dataUpdatedAtHash = result.map((item) => item.dataUpdatedAt).join('')
  useEffect(() => {
    if (dataUpdatedAt) {
      setDataUpdatedAt(new Date().getTime())
      return
    }
    const notAllDataLoaded = result.some((item) => !item.dataUpdatedAt)
    if (!notAllDataLoaded) {
      setDataUpdatedAt(new Date().getTime())
    }
  }, [dataUpdatedAtHash])
  const isInitialLoading = !dataUpdatedAt

  return { data, dataUpdatedAt, isInitialLoading }
}
